import {
  EDITOR_COLLAPSE_TIMEOUT_MS,
  EDITOR_COLLAPSING_BODY_SELECTOR,
  EDITOR_CONTROL_PANEL_BAR_ID,
  EDITOR_SCROLL_IDLE_DELAY_MS,
  EDITOR_SCROLL_IDLE_TIMEOUT_MS,
} from '../constants/editor'
import { toPlaySelector } from '../utils/editorPlay'
import { getStickyHeaderSpace } from '../utils/stickyHeader'

export const useEditorScroll = () => {
  const { scrollTop } = useScrollUtils()

  const getCollapsingHeightAbove = (top: number) =>
    [...document.querySelectorAll(EDITOR_COLLAPSING_BODY_SELECTOR)]
      .map((body) => body.getBoundingClientRect())
      .filter((rect) => rect.top < top)
      .reduce((total, { height }) => total + height, 0)

  const toDocumentTop = (element: HTMLElement) => {
    const { top } = element.getBoundingClientRect()

    return top + window.scrollY - getCollapsingHeightAbove(top)
  }

  const getVisibleBottom = () => {
    const bar = document.getElementById(EDITOR_CONTROL_PANEL_BAR_ID)
    if (!bar) return window.innerHeight

    const { bottom, height } = bar.getBoundingClientRect()
    return window.innerHeight - height - Math.max(0, window.innerHeight - bottom)
  }

  const findPlay = async (playId: string) => {
    await nextTick()
    return document.querySelector<HTMLElement>(toPlaySelector(playId))
  }

  const toHeaderAlignedTop = (element: HTMLElement) =>
    Math.max(0, toDocumentTop(element) - getStickyHeaderSpace())

  const scrollToElement = (element: Maybe<HTMLElement>) => {
    if (!element) return

    scrollTop(toHeaderAlignedTop(element))
  }

  const scrollToPlay = async (playId: string, behavior: ScrollBehavior) => {
    const play = await findPlay(playId)
    if (!play) return

    scrollTop(toHeaderAlignedTop(play), behavior)
  }

  const toCenteredTop = (element: HTMLElement) => {
    const { height } = element.getBoundingClientRect()
    const viewportCenter = (getStickyHeaderSpace() + getVisibleBottom()) / 2

    return toDocumentTop(element) + height / 2 - viewportCenter
  }

  // `below` is the space to keep visible under the play, not fitting centered aligns the play below the header
  const scrollToCenteredPlay = async (playId: Maybe<string>, below = 0) => {
    if (!playId) return

    const play = await findPlay(playId)
    if (!play) return

    const headerAlignedTop = toHeaderAlignedTop(play)
    const centeredTop = toCenteredTop(play)
    const fittingTop =
      toDocumentTop(play) + play.getBoundingClientRect().height + below - getVisibleBottom()

    // near the page top the play cannot reach the center and would stay wherever it is
    const top =
      Math.max(0, fittingTop) > centeredTop
        ? headerAlignedTop
        : Math.min(centeredTop, headerAlignedTop)

    // near the page end it cannot reach its position either, so the page grows until released
    releaseSpaceBelow()
    const missing = top - (document.documentElement.scrollHeight - window.innerHeight)
    if (missing > 0) document.body.style.paddingBottom = `${missing}px`

    scrollTop(top)
  }

  const releaseSpaceBelow = () => {
    document.body.style.paddingBottom = ''
  }

  const waitForScrollIdle = () =>
    new Promise<void>((resolve) => {
      let settleTimer: ReturnType<typeof setTimeout>

      const finish = () => {
        clearTimeout(settleTimer)
        clearTimeout(guardTimer)
        window.removeEventListener('scroll', settle)
        resolve()
      }

      const settle = () => {
        clearTimeout(settleTimer)
        settleTimer = setTimeout(finish, EDITOR_SCROLL_IDLE_DELAY_MS)
      }

      const guardTimer = setTimeout(finish, EDITOR_SCROLL_IDLE_TIMEOUT_MS)

      window.addEventListener('scroll', settle, { passive: true })
      settle()
    })

  // a collapsing card still shrinks the page, which moves every play below it
  const waitForCollapsed = async () => {
    const deadline = Date.now() + EDITOR_COLLAPSE_TIMEOUT_MS
    await nextTick()
    while (document.querySelector(EDITOR_COLLAPSING_BODY_SELECTOR) && Date.now() < deadline) {
      await new Promise(requestAnimationFrame)
    }
  }

  return {
    releaseSpaceBelow,
    scrollToCenteredPlay,
    scrollToElement,
    scrollToPlay,
    waitForCollapsed,
    waitForScrollIdle,
  }
}
